import { BasicAuthServiceHelper } from '@/modules/Auth'
import { AppDispatch } from '@/store'
import { FavoritesTypes } from './FavoritesReducer'
import { DeleteFavorite, GetFavorites, PostFavorite } from './apiTypes'
import { flattenHydraCollection, IRI } from 'emt-library'
import Constants from '@/assets/Constants'
import {
  FavoriteAPIType,
  FavoriteType,
  setFavoriteAPIType,
  setFavoriteType,
} from '@/modules/Favorites/apiTypes/GetFavorites'
import { UserCard } from '@/dataV3/Search/UserCard'

/* *******************************
  GET FAVORITES
******************************* */

export const dispatchGetFavoritesAsync =
  (dispatch: AppDispatch) =>
  async (params: { pageUrlPath: string } | { searchParams: GetFavorites.SearchParams }): Promise<UserCard[]> => {
    const rawResponse =
      'pageUrlPath' in params
        ? await BasicAuthServiceHelper.requestAuthenticatedV2Async<GetFavorites.Response>(
            GetFavorites.NextRoute(params.pageUrlPath),
          )
        : await BasicAuthServiceHelper.requestAuthenticatedV2Async<
            GetFavorites.Response,
            never,
            never,
            GetFavorites.SearchParams
          >(GetFavorites.Route, { searchParams: params.searchParams })

    const response = flattenHydraCollection(rawResponse)

    let isFirstPage = false
    let mode: FavoriteAPIType
    if ('searchParams' in params) {
      mode = params.searchParams.type
      isFirstPage = true
    } else {
      const url = new URL(params.pageUrlPath, Constants.apiUrl)
      mode = url.searchParams.get('type') as FavoriteAPIType
    }

    dispatch({
      type: FavoritesTypes.UpdateFavorites,
      payload: {
        totalCount: response.totalItems,
        favorites: response.member,
        mode: setFavoriteAPIType(mode),
        nextPage: response.nextPage || null,
        isFirstPage,
      },
    })

    return response.member
  }

/* *******************************
  POST FAVORITE
******************************* */

export const dispatchPostFavoriteAsync =
  (dispatch: AppDispatch) =>
  async (favoriteIRI: IRI): Promise<UserCard> => {
    const response = await BasicAuthServiceHelper.requestAuthenticatedV2Async<PostFavorite.Response, PostFavorite.Body>(
      PostFavorite.Route(favoriteIRI),
    )

    await dispatchGetFavoritesAsync(dispatch)({ searchParams: { type: setFavoriteType(FavoriteType.sent) } })
    await dispatchGetFavoritesAsync(dispatch)({ searchParams: { type: setFavoriteType(FavoriteType.mutual) } })
    await dispatchGetFavoritesAsync(dispatch)({ searchParams: { type: setFavoriteType(FavoriteType.received) } })

    return response
  }

/* *******************************
  DELETE FAVORITE
******************************* */

export const dispatchDeleteFavoriteAsync =
  (dispatch: AppDispatch) =>
  async (favoriteIRI: IRI): Promise<void> => {
    await BasicAuthServiceHelper.requestAuthenticatedV2Async<DeleteFavorite.Response>(DeleteFavorite.Route(favoriteIRI))

    const favoriteId = favoriteIRI.split('/').slice(-2)[0]
    if (favoriteId) {
      dispatch({ type: FavoritesTypes.DeleteFavorite, payload: { favoriteId } })
    }
    await dispatchGetFavoritesAsync(dispatch)({ searchParams: { type: setFavoriteType(FavoriteType.sent) } })
    await dispatchGetFavoritesAsync(dispatch)({ searchParams: { type: setFavoriteType(FavoriteType.mutual) } })
    await dispatchGetFavoritesAsync(dispatch)({ searchParams: { type: setFavoriteType(FavoriteType.received) } })
  }
